import { Box, Skeleton } from "@mui/material"
import type { FinancialOverview } from "@crm-src/types/financial-metrics"
import { StatsCard } from "@crm-src/components/stats-card"
import { formatCurrency } from "@crm-src/lib/utils"
import { QuoteStatusBreakdown } from "./quote-status-breakdown"
import { DollarSign, TrendingUp, FileText, Clock } from "lucide-react"

interface FinancialOverviewTabProps {
  data: FinancialOverview
  isLoading?: boolean
}

export function FinancialOverviewTab({ data, isLoading }: FinancialOverviewTabProps) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ height: 128 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" sx={{ height: 256 }} />
      </Box>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Top Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Total Revenue"
          value={formatCurrency(data.revenue.total, { decimals: 0 })}
          icon={DollarSign}
          iconColor="text-green-600"
          valueColor="text-green-600"
        />

        <StatsCard
          title="Expected Future Income"
          value={formatCurrency(data.expected_future_income.total, { decimals: 0 })}
          icon={TrendingUp}
          iconColor="text-blue-600"
          valueColor="text-blue-600"
        />

        <StatsCard
          title="Quote Conversion Rate"
          value={`${data.conversion_rate.conversion_rate}%`}
          icon={FileText}
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />

        <StatsCard
          title="Pending Quotes"
          value={data.quotes.pending.count}
          icon={Clock}
          iconColor="text-orange-600"
          valueColor="text-orange-600"
        />
      </Box>

      {/* Quote Status Breakdown */}
      <QuoteStatusBreakdown data={data.quotes} />

      {/* Expected Income Breakdown */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        <StatsCard
          title="Unpaid Invoices"
          value={formatCurrency(data.expected_future_income.unpaid_invoices, { decimals: 0 })}
          icon={FileText}
          iconColor="text-orange-600"
          valueColor="text-orange-600"
        />
        <StatsCard
          title="Partial Payments Due"
          value={formatCurrency(data.expected_future_income.partial_payments_remaining, { decimals: 0 })}
          icon={DollarSign}
          iconColor="text-blue-600"
          valueColor="text-blue-600"
        />
        <StatsCard
          title="Pending Quote Value"
          value={formatCurrency(data.expected_future_income.pending_quotes, { decimals: 0 })}
          icon={TrendingUp}
          iconColor="text-purple-600"
          valueColor="text-purple-600"
        />
      </Box>
    </Box>
  )
}



